import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose, Button } from '@shopmuchmore/design-system';
import { usePWAInstall, PWA_STORAGE_KEYS } from '../hooks/usePWAInstall';
import { MdIosShare, MdAddBox } from 'react-icons/md';
import { Download, Monitor, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';

export function PWAInstallPrompt() {
  const { isInstallable, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const [showGenericDialog, setShowGenericDialog] = useState(false);

  useEffect(() => {
    const handleShowInstall = async () => {
      const storedInstalled = localStorage.getItem(PWA_STORAGE_KEYS.INSTALLED) === 'true';
      if (isInstalled || storedInstalled) {
        toast.info('ShopMuchMore is already installed on your device!');
        return;
      }

      if (isInstallable) {
        const success = await promptInstall();
        if (!success) {
          if (isIOS) {
            setShowIOSDialog(true);
          } else {
            setShowGenericDialog(true);
          }
        }
      } else if (isIOS) {
        setShowIOSDialog(true);
      } else {
        setShowGenericDialog(true);
      }
    };

    window.addEventListener('show-pwa-install', handleShowInstall);

    // Only schedule automatic banner popup if not dismissed and not installed
    const isDismissed = localStorage.getItem(PWA_STORAGE_KEYS.DISMISSED) === 'true';
    const isStoredInstalled = localStorage.getItem(PWA_STORAGE_KEYS.INSTALLED) === 'true';

    let timer: ReturnType<typeof setTimeout> | null = null;
    if (!isInstalled && !isStoredInstalled && !isDismissed) {
      timer = setTimeout(() => {
        const currentlyDismissed = localStorage.getItem(PWA_STORAGE_KEYS.DISMISSED) === 'true';
        const currentlyInstalled = localStorage.getItem(PWA_STORAGE_KEYS.INSTALLED) === 'true';
        if (!isInstalled && !currentlyInstalled && !currentlyDismissed) {
          if (isInstallable || isIOS) {
            setShowBanner(true);
          }
        }
      }, 3000);
    }

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('show-pwa-install', handleShowInstall);
    };
  }, [isInstallable, isInstalled, isIOS, promptInstall]);

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem(PWA_STORAGE_KEYS.DISMISSED, 'true');
  };

  const handleInstallClick = async () => {
    if (isInstallable) {
      setShowBanner(false);
      const success = await promptInstall();
      if (!success) {
        if (isIOS) {
          setShowIOSDialog(true);
        } else {
          setShowGenericDialog(true);
        }
      }
    } else if (isIOS) {
      setShowIOSDialog(true);
      setShowBanner(false);
    } else {
      setShowGenericDialog(true);
      setShowBanner(false);
    }
  };

  return (
    <>
      {/* Auto Banner */}
      {showBanner && (
        <div className="fixed bottom-4 left-4 right-4 md:bottom-8 md:left-auto md:right-8 md:w-96 bg-white rounded-2xl border border-gray-200 p-4 z-50 flex flex-col gap-3 animate-in slide-in-from-bottom-5">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Install ShopMuchMore App</h3>
              <p className="text-xs text-gray-500 mt-1">Get the full native app experience on your device.</p>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <span className="sr-only">Close</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleInstallClick} className="w-full text-xs py-2 h-9 rounded-xl" size="sm">
              Install App
            </Button>
          </div>
        </div>
      )}

      {/* iOS Instructions Dialog */}
      <Dialog
        open={showIOSDialog}
        onOpenChange={(open) => {
          setShowIOSDialog(open);
          if (!open) {
            localStorage.setItem(PWA_STORAGE_KEYS.DISMISSED, 'true');
          }
        }}
      >
        <DialogContent className="sm:max-w-md rounded-2xl border-gray-200">
          <DialogHeader className="text-center sm:text-center pb-1">
            <DialogTitle className="text-lg font-bold text-gray-900">Install App on iOS</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Install the ShopMuchMore app on your iPhone or iPad for quick access.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center justify-center space-y-3 py-3">
            <div className="flex items-start gap-3.5 text-gray-700 bg-gray-50/80 p-3.5 rounded-2xl w-full border border-gray-100">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white font-bold shrink-0 text-xs mt-0.5">1</span>
              <div className="flex-1 text-sm leading-relaxed text-left text-gray-700">
                Tap the <MdIosShare className="inline-block w-4 h-4 text-blue-500 mx-1 mb-0.5" /> share icon in your Safari browser bar.
              </div>
            </div>
            
            <div className="flex items-start gap-3.5 text-gray-700 bg-gray-50/80 p-3.5 rounded-2xl w-full border border-gray-100">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white font-bold shrink-0 text-xs mt-0.5">2</span>
              <div className="flex-1 text-sm leading-relaxed text-left text-gray-700">
                Scroll down and tap <strong className="font-semibold text-gray-900"><MdAddBox className="inline-block w-4 h-4 text-gray-600 mx-1 mb-0.5" /> Add to Home Screen</strong>.
              </div>
            </div>
          </div>

          <DialogFooter className="sm:justify-end pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" className="rounded-xl px-6 h-10 border-gray-200 font-semibold text-sm">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Desktop / Generic Browser Instructions Dialog */}
      <Dialog
        open={showGenericDialog}
        onOpenChange={(open) => {
          setShowGenericDialog(open);
          if (!open) {
            localStorage.setItem(PWA_STORAGE_KEYS.DISMISSED, 'true');
          }
        }}
      >
        <DialogContent className="sm:max-w-md rounded-2xl border-gray-200">
          <DialogHeader className="text-center sm:text-center pb-1">
            <DialogTitle className="text-lg font-bold text-gray-900">Install ShopMuchMore App</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Install the app directly in your browser for a faster, full-screen shopping experience.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center justify-center space-y-3 py-3">
            <div className="flex items-start gap-3.5 text-gray-700 bg-gray-50/80 p-3.5 rounded-2xl w-full border border-gray-100">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white font-bold shrink-0 text-xs mt-0.5">1</span>
              <div className="flex-1 text-sm leading-relaxed text-left text-gray-700">
                Look for the <Download className="inline-block w-4 h-4 text-primary mx-1 mb-0.5" /> or <Monitor className="inline-block w-4 h-4 text-primary mx-1 mb-0.5" /> <strong className="font-semibold text-gray-900">Install</strong> icon in your browser address bar.
              </div>
            </div>
            
            <div className="flex items-start gap-3.5 text-gray-700 bg-gray-50/80 p-3.5 rounded-2xl w-full border border-gray-100">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white font-bold shrink-0 text-xs mt-0.5">2</span>
              <div className="flex-1 text-sm leading-relaxed text-left text-gray-700">
                Alternatively, open your browser menu (<MoreVertical className="inline-block w-4 h-4 text-gray-600 mx-0.5 mb-0.5" />) and click <strong className="font-semibold text-gray-900">&quot;Install ShopMuchMore&quot;</strong> or <strong className="font-semibold text-gray-900">&quot;Add to Home screen&quot;</strong>.
              </div>
            </div>
          </div>

          <DialogFooter className="sm:justify-end pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" className="rounded-xl px-6 h-10 border-gray-200 font-semibold text-sm">
                Got it
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
